import {
  MODULE_DESCRIPTIONS,
  MODULE_TREE,
  type ModuleId,
  type ModuleNode,
  moduleIdsOf,
} from '@/lib/modules';
import { useEffect, useRef, useState } from 'react';

/**
 * Strom oprávnění — každá položka menu se povoluje zvlášť. Skupiny se dají
 * rozbalit a zaškrtnutím se zapne/vypne celý jejich obsah; když je vybraná jen
 * část, svítí skupina pomlčkou. Bez nadřazeného modulu („Výroba") se uživatel
 * do systému nedostane, takže se rodič drží zaškrtnutý s jakýmkoli potomkem.
 */
export function ModuleTree({
  selected,
  onChange,
}: {
  selected: ModuleId[];
  onChange: (next: ModuleId[]) => void;
}) {
  const groups = MODULE_TREE.filter((n) => (n.children?.length ?? 0) > 0).map((n) => n.label);
  const [open, setOpen] = useState<Set<string>>(new Set(groups));
  const allOpen = groups.every((g) => open.has(g));

  return (
    <div>
      {groups.length > 0 && (
        <button
          type="button"
          onClick={() => setOpen(allOpen ? new Set() : new Set(collectGroups(MODULE_TREE)))}
          className="mb-2 text-xs font-semibold text-text-secondary border border-border rounded-md px-3 py-1.5 hover:bg-surface-hover cursor-pointer"
        >
          {allOpen ? 'Sbalit vše' : 'Rozbalit vše'}
        </button>
      )}
      <div className="space-y-1.5">
        {MODULE_TREE.map((node) => (
          <NodeRow
            key={node.id ?? node.label}
            node={node}
            depth={0}
            selected={selected}
            onChange={onChange}
            open={open}
            setOpen={setOpen}
          />
        ))}
      </div>
    </div>
  );
}

/** Popisky všech skupin ve stromu (pro „Rozbalit vše"). */
function collectGroups(nodes: ModuleNode[]): string[] {
  const out: string[] = [];
  for (const n of nodes) {
    if (n.children?.length) {
      out.push(n.label);
      out.push(...collectGroups(n.children));
    }
  }
  return out;
}

function NodeRow({
  node,
  depth,
  selected,
  onChange,
  open,
  setOpen,
}: {
  node: ModuleNode;
  depth: number;
  selected: ModuleId[];
  onChange: (next: ModuleId[]) => void;
  open: Set<string>;
  setOpen: (fn: (prev: Set<string>) => Set<string>) => void;
}) {
  const kids = node.children ?? [];
  const all = moduleIdsOf(node);
  const own = node.id;
  const leaves = all.filter((id) => id !== own);
  const checkedLeaves = leaves.filter((id) => selected.includes(id));
  const checked = own
    ? selected.includes(own)
    : leaves.length > 0 && checkedLeaves.length === leaves.length;
  const partial =
    leaves.length > 0 && checkedLeaves.length > 0 && checkedLeaves.length < leaves.length;
  const expanded = open.has(node.label);

  function toggle(on: boolean) {
    const next = new Set(selected);
    for (const id of all) {
      if (on) next.add(id);
      else next.delete(id);
    }
    // Rodičovský modul drží přístup k celé sekci — bez potomka nemá smysl,
    // s jakýmkoli potomkem naopak musí zůstat zapnutý.
    syncParents(next);
    onChange([...next]);
  }

  return (
    <div>
      <div
        className={`flex items-start gap-2 p-2 border rounded-md transition-colors ${
          checked || partial ? 'border-primary/40 bg-primary-50' : 'border-border'
        }`}
      >
        {kids.length > 0 ? (
          <button
            type="button"
            onClick={() =>
              setOpen((prev) => {
                const next = new Set(prev);
                if (next.has(node.label)) next.delete(node.label);
                else next.add(node.label);
                return next;
              })
            }
            aria-label={expanded ? 'Sbalit' : 'Rozbalit'}
            className="mt-0.5 h-4 w-4 shrink-0 text-text-muted hover:text-text cursor-pointer"
          >
            {expanded ? '▾' : '▸'}
          </button>
        ) : (
          <span className="w-4 shrink-0" />
        )}
        <Check checked={checked} partial={partial} onChange={toggle} />
        <span className="flex-1 min-w-0">
          <span
            className={`block text-sm text-text ${depth === 0 ? 'font-semibold' : 'font-medium'}`}
          >
            {node.label}
          </span>
          {own && MODULE_DESCRIPTIONS[own] && (
            <span className="block text-[11px] text-text-muted leading-tight mt-0.5">
              {MODULE_DESCRIPTIONS[own]}
            </span>
          )}
        </span>
      </div>

      {kids.length > 0 && expanded && (
        <div className="ml-6 mt-1.5 space-y-1.5 border-l border-border-light pl-3">
          {kids.map((child) => (
            <NodeRow
              key={child.id ?? child.label}
              node={child}
              depth={depth + 1}
              selected={selected}
              onChange={onChange}
              open={open}
              setOpen={setOpen}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/** Rodič je zapnutý, právě když má aspoň jednoho zaškrtnutého potomka. */
function syncParents(set: Set<ModuleId>): void {
  const walk = (node: ModuleNode): boolean => {
    const kidsOn = (node.children ?? []).map(walk).some(Boolean);
    if (!node.id) return kidsOn;
    if (node.children?.length) {
      if (kidsOn) set.add(node.id);
      else set.delete(node.id);
      return kidsOn;
    }
    return set.has(node.id);
  };
  for (const n of MODULE_TREE) walk(n);
}

/** Zaškrtávátko se třetím stavem „část" (skupina s některými položkami). */
function Check({
  checked,
  partial = false,
  onChange,
}: {
  checked: boolean;
  partial?: boolean;
  onChange: (on: boolean) => void;
}) {
  const ref = useRef<HTMLInputElement>(null);
  // Třetí stav umí jen DOM, ne atribut — nastavuje se po každém překreslení.
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = partial && !checked;
  }, [partial, checked]);

  return (
    <span className="relative mt-0.5 inline-flex h-[18px] w-[18px] shrink-0">
      <input
        ref={ref}
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="absolute inset-0 z-10 m-0 h-full w-full cursor-pointer opacity-0"
      />
      <span
        className={`pointer-events-none flex h-full w-full items-center justify-center rounded-[4px] border transition-colors ${
          checked || partial ? 'border-primary bg-primary text-white' : 'border-[#cbd5e1] bg-white'
        }`}
      >
        {partial && !checked ? (
          <span className="block h-0.5 w-2.5 bg-white" />
        ) : checked ? (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M20 6L9 17l-5-5" />
          </svg>
        ) : null}
      </span>
    </span>
  );
}
